import React from "react";
import { useInView } from "react-intersection-observer";

import { Moment } from "moment";

import { PendingCoverCropSuggestion } from "services";

import { useGetFieldShape, useTranslation } from "utils";

import { Coordinates } from "models";

import { Flex, Form, Icon, KlimRadioGroup, MapStatic, RangePicker, Tag, TagColour, Typography } from "components";

import {
  CoverCropCardState,
  CoverCropType,
  DraftDecision,
  getCardState,
  getSeedingPickerOpenDate,
  isDraftAbsent,
} from "pages/app/all-field-view/components/tasksDrawer/coverCropDetectionUtils";

const STATE_COLOUR: Record<CoverCropCardState, TagColour> = {
  pending: "blue",
  complete: "green",
  skipped: "grey",
};

const COVER_CROP_TYPES: CoverCropType[] = ["simple", "complex", "none"];

type CoverCropSuggestionCardProps = {
  suggestion: PendingCoverCropSuggestion;
  draft?: DraftDecision;
  fieldLocation?: Coordinates;
  fieldSize?: number;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  onChange: (changes: Partial<DraftDecision>) => void;
};

export const CoverCropSuggestionCard = ({
  suggestion,
  draft,
  fieldLocation,
  fieldSize,
  isExpanded,
  onToggleExpanded,
  onChange,
}: CoverCropSuggestionCardProps) => {
  // useSuspense: false — a Suspense remount while translations load would close the drawer mid-answer.
  const { t } = useTranslation("allFieldView", { useSuspense: false });
  const { ref, inView } = useInView({ triggerOnce: true });
  const { fieldShape, fieldShapeLoading } = useGetFieldShape({ fieldId: suggestion.fieldId, enabled: inView && !!fieldLocation });

  const { detectionId } = suggestion;
  const state = getCardState(draft);
  const hasCoverCrop = draft?.type === "simple" || draft?.type === "complex";
  const isAbsent = isDraftAbsent(draft);
  const seedingPickerOpenDate = getSeedingPickerOpenDate(suggestion);
  const harvestPickerOpenDate = draft?.seedingStart ?? seedingPickerOpenDate;

  return (
    <Flex vertical ref={ref} className={`cover-crop-suggestion-card${isAbsent ? " no-cover-crop" : ""}`} data-testid={`cover-crop-suggestion-${detectionId}`}>
      <Flex
        align="center"
        justify="space-between"
        gap="1rem"
        className="cover-crop-suggestion-card-header"
        onClick={onToggleExpanded}
        data-testid={`cover-crop-suggestion-header-${detectionId}`}
      >
        <Flex align="center" gap="0.75rem" flex={1} className="cover-crop-suggestion-card-identity">
          <div className="cover-crop-suggestion-card-icon">
            {fieldLocation && !fieldShapeLoading ? (
              /* @ts-expect-error MapStatic needs TS conversion */
              <MapStatic field={{ id: suggestion.fieldId, location: fieldLocation, size: fieldSize }} geometry={fieldShape} location={fieldLocation} />
            ) : (
              <Flex align="center" justify="center" className="cover-crop-suggestion-card-icon-flex">
                <Icon iconName="tabler/satellite" size="m" />
              </Flex>
            )}
          </div>
          <Typography variant="paragraph-m" className="margin-0">
            {suggestion.fieldName}
          </Typography>
        </Flex>
        <Tag colour={STATE_COLOUR[state]} text={t(`coverCropDetectionDrawer.state.${state}`)} dataTestId={`cover-crop-suggestion-state-${detectionId}`} />
        <Icon iconName={isExpanded ? "tabler/chevron-up" : "tabler/chevron-down"} size="s" />
      </Flex>

      {isExpanded && (
        <Form layout="vertical">
          <Flex vertical gap="0.75rem" className="cover-crop-suggestion-card-body">
            <Typography variant="paragraph-m" className="margin-0">
              {t("coverCropDetectionDrawer.typeQuestion")}
            </Typography>
            <KlimRadioGroup
              className="radio-bg-white cover-crop-type-group"
              size="large"
              type="tertiary"
              dataTestId={`cover-crop-type-${detectionId}`}
              options={COVER_CROP_TYPES.map((value) => ({
                label: <Typography variant="paragraph-m">{t(`coverCropDetectionDrawer.type.${value}`)}</Typography>,
                value,
              }))}
              value={draft?.type}
              optionType="button"
              buttonStyle="solid"
              onChange={(event) => onChange({ type: event.target.value as CoverCropType })}
            />

            {hasCoverCrop && (
              <>
                <Flex gap="1rem" className="cover-crop-suggestion-dates">
                  <RangePicker
                    dataTestId={`cover-crop-seeding-${detectionId}`}
                    label={t("coverCropDetectionDrawer.seedingRange")}
                    name={[`seedingRange-${detectionId}`]}
                    format={["DD.MM.YYYY"]}
                    initialValue={[draft?.seedingStart ?? null, draft?.seedingEnd ?? null]}
                    defaultPickerValue={[seedingPickerOpenDate, seedingPickerOpenDate]}
                    onChange={([start, end]: [Moment | null, Moment | null]) => onChange({ seedingStart: start ?? undefined, seedingEnd: end ?? undefined })}
                  />
                  <Typography variant="paragraph-m" className="margin-0 cover-crop-dates-separator">
                    —
                  </Typography>
                  <RangePicker
                    dataTestId={`cover-crop-harvest-${detectionId}`}
                    label={t("coverCropDetectionDrawer.harvestRange")}
                    name={[`harvestRange-${detectionId}`]}
                    format={["DD.MM.YYYY"]}
                    initialValue={[draft?.harvestStart ?? null, draft?.harvestEnd ?? null]}
                    defaultPickerValue={[harvestPickerOpenDate, harvestPickerOpenDate]}
                    onChange={([start, end]: [Moment | null, Moment | null]) => onChange({ harvestStart: start ?? undefined, harvestEnd: end ?? undefined })}
                  />
                </Flex>

                <Flex align="center" justify="space-between" gap="1rem">
                  <Typography variant="paragraph-m" className="margin-0">
                    {t("coverCropDetectionDrawer.hasResidueQuestion")}
                  </Typography>
                  <KlimRadioGroup
                    className="radio-bg-white"
                    size="large"
                    type="tertiary"
                    dataTestId={`cover-crop-has-residue-${detectionId}`}
                    options={[
                      { label: <Typography variant="paragraph-m">{t("cropForm.yes")}</Typography>, value: true },
                      { label: <Typography variant="paragraph-m">{t("cropForm.no")}</Typography>, value: false },
                    ]}
                    value={draft?.hasResidue}
                    optionType="button"
                    buttonStyle="solid"
                    onChange={(event) => onChange({ hasResidue: event.target.value as boolean })}
                  />
                </Flex>
              </>
            )}
          </Flex>
        </Form>
      )}
    </Flex>
  );
};
